import type {
  Bundle,
  BundleEntry,
  CarePlan,
  CarePlanActivity,
  Coding,
  Extension,
  Goal,
  PlanDefinition,
  PlanDefinitionAction,
  ServiceRequest,
} from '@medplum/fhirtypes';
import { z } from 'zod';

import { withAgentTag } from '@eluve/fhir-review-lifecycle';

import {
  ACTIVITY_PHASE_EXTENSION_URL,
  ACTIVITY_STICK_EXTENSION_URL,
  CAREPLAN_PHASE_SEGMENT_CODE,
  CAREPLAN_SEGMENT_SYSTEM,
  ELUVE_PHASE_SYSTEM,
  slugify,
} from './constants';
import {
  actionDurationDays,
  actionVisitTarget,
  activityPhaseCoding,
  addDays,
  diffDays,
  instanceActivitiesByPhase,
  isSpineStick,
  phaseCoding,
  planCanonical,
  serviceRequestSegmentPhaseCoding,
} from './instance';
import { currentPhaseCadence } from './phase-cadence';
import {
  reassessmentMarkerExtension,
  reassessmentMarkerOf,
  reassessmentMarkerSchema,
} from './reassessment-marker';
import { planShapeOf } from './registry';
import {
  SESSION_FREQUENCY_EXTENSION_URL,
  frequencySegmentSchema,
  sessionFrequencyExtension,
  sessionFrequencyOf,
} from './session-frequency';
import {
  type DecodeContext,
  fromCodeableConcept,
  fromDosageExtension,
  fromFhirGoal,
  fromFhirPathStringLiteral,
  fromGoalTarget,
  fromTiming,
  isActivityAction,
  toDosageExtension,
  toGoalTarget,
  toTiming,
} from './shared';
import { goalSchema, planActivitySchema } from './spec';
import type { GoalTarget, PlanGoal } from './spec';

export const instanceActivitySchema = planActivitySchema.extend({
  template: z
    .string()
    .optional()
    .describe(
      'Canonical URL of the template activity this derives from. Echo it VERBATIM from the seed for template-derived activities (even when you adjust dosage/frequency); OMIT it for new ad-hoc activities. Never invent or alter one.',
    ),
});

export type InstanceActivitySpec = z.infer<typeof instanceActivitySchema>;

export const instancePhaseSpecSchema = z.object({
  title: z
    .string()
    .describe(
      "Phase title from the template, e.g. 'Phase 2 — Progressive Loading'.",
    ),
  code: z
    .string()
    .optional()
    .describe(
      'Stable phase identifier from the template. Echo verbatim; never invent or change.',
    ),
  description: z
    .string()
    .optional()
    .describe('What happens in this phase for THIS patient.'),
  durationDays: z
    .number()
    .int()
    .min(0)
    .optional()
    .describe(
      'Phase duration in days for this patient. Shift it to fit their schedule; 0 skips the phase entirely. Omit when the phase is purely goal-gated.',
    ),
  visitsToComplete: z
    .number()
    .int()
    .min(1)
    .optional()
    .describe(
      "Target number of visits to complete this phase, from the template's exit gate (`visitCount`). Echo it from the seed; adjust only if this patient's cadence genuinely differs.",
    ),
  activities: z
    .array(instanceActivitySchema)
    .optional()
    .describe(
      'The activities prescribed to this patient during this phase. Omit a template activity to skip it; adjust dosage/frequency to re-dose it; add entries (without `template`) for ad-hoc activities.',
    ),
});

export type InstancePhaseSpec = z.infer<typeof instancePhaseSpecSchema>;

export const phasedInstanceSpecSchema = z.object({
  shape: z
    .literal('phased')
    .describe("The template's Plan Shape. Only phased plans are tailorable."),
  title: z
    .string()
    .describe(
      "The plan title — defaults to the template's; rarely needs changing.",
    ),
  templateCanonical: z
    .string()
    .describe(
      'Pinned canonical (`url|version`) of the template PlanDefinition. Echo verbatim.',
    ),
  phaseCodeSystem: z
    .string()
    .optional()
    .describe(
      'Code system of the phase identifiers. Echo verbatim from the seed.',
    ),
  startDate: z
    .string()
    .describe(
      'ISO date (YYYY-MM-DD) the plan starts. Phase schedules walk forward from it.',
    ),
  goals: z
    .array(goalSchema)
    .optional()
    .describe(
      "The patient's goals. Personalize targets against their baseline; drop or add goals as agreed.",
    ),
  sessionFrequency: z
    .array(frequencySegmentSchema)
    .optional()
    .describe(
      'Visit cadence segments for this patient (per-plan snapshot). Echo from the seed unless the cadence genuinely changes.',
    ),
  reassessment: reassessmentMarkerSchema
    .optional()
    .describe(
      'When to reassess — after N visits or N days. Echo from the seed unless the visit genuinely moves it.',
    ),
  phases: z
    .array(instancePhaseSpecSchema)
    .min(1)
    .describe(
      "The template's phase spine — SAME phases, SAME order, always all of them. The spine is the template's identity: never add, remove, merge, or reorder phases.",
    ),
});

export type PhasedInstanceSpec = z.infer<typeof phasedInstanceSpecSchema>;

// A non-phased (continuous/episodic) instance is a VERBATIM projection of the
// template onto a patient: no per-patient divergence, so no phase spine, no
// per-activity ServiceRequests, no diff (ADR-0013, ENG-4773). It carries only
// identity + start + goals — and because continuous control bands are already
// FHIR goals on the template, those ride along here too, so an accepted plan
// isn't an empty shell. Rendered read-only from the template; not tailorable.
const verbatimInstanceFields = {
  title: z
    .string()
    .describe("The plan title — the template's, bound to this patient."),
  templateCanonical: z
    .string()
    .describe(
      'Pinned canonical (`url|version`) of the template PlanDefinition.',
    ),
  startDate: z.string().describe('ISO date (YYYY-MM-DD) the plan starts.'),
  goals: z
    .array(goalSchema)
    .optional()
    .describe(
      "The plan's goals — for continuous plans this also carries the control bands as ranged goals.",
    ),
};

export const continuousInstanceSpecSchema = z.object({
  shape: z.literal('continuous'),
  ...verbatimInstanceFields,
});

export const episodicInstanceSpecSchema = z.object({
  shape: z.literal('episodic'),
  ...verbatimInstanceFields,
});

export const planInstanceSpecSchema = z.discriminatedUnion('shape', [
  phasedInstanceSpecSchema,
  continuousInstanceSpecSchema,
  episodicInstanceSpecSchema,
]);

export type PlanInstanceSpec = z.infer<typeof planInstanceSpecSchema>;
export type VerbatimInstanceSpec =
  | z.infer<typeof continuousInstanceSpecSchema>
  | z.infer<typeof episodicInstanceSpecSchema>;

// --- Seed: verbatim template → turn-zero spec --------------------------------

export type SeedInstanceArgs = {
  // ISO date (YYYY-MM-DD) the plan starts.
  startDate: string;
};

function pinTemplateCanonical(
  canonical: string | undefined,
  adVersion: string | undefined,
): string | undefined {
  if (!canonical) {
    return undefined;
  }
  if (canonical.includes('|')) {
    return canonical;
  }
  return adVersion ? `${canonical}|${adVersion}` : canonical;
}

function seedActivities(
  actions: PlanDefinitionAction[] | undefined,
  ctx: DecodeContext | undefined,
): InstanceActivitySpec[] | undefined {
  const adByUrl = new Map(
    (ctx?.activityDefinitions ?? []).flatMap((ad) =>
      ad.url ? [[ad.url, ad] as const] : [],
    ),
  );
  const out: InstanceActivitySpec[] = [];
  for (const action of actions ?? []) {
    if (!isActivityAction(action)) {
      continue;
    }
    const canonical = action.definitionCanonical;
    const bare = canonical?.split('|')[0];
    const ad = bare ? adByUrl.get(bare) : undefined;
    const name = action.title ?? ad?.title ?? ad?.name;
    if (!name) {
      continue;
    }
    out.push({
      name,
      code: fromCodeableConcept(ad?.code),
      description: ad?.description,
      dosage:
        fromDosageExtension(action.extension) ??
        fromDosageExtension(ad?.extension),
      frequency:
        fromTiming(action.timingTiming).cadence ??
        fromTiming(ad?.timingTiming).cadence,
      patientInstructions: fromFhirPathStringLiteral(
        ad?.dynamicValue?.find((d) => d.path === 'patientInstruction')
          ?.expression?.expression,
      ),
      template: pinTemplateCanonical(canonical, ad?.version),
    });
  }
  return out.length > 0 ? out : undefined;
}

// The untailored instantiation: the template projected onto a patient-agnostic
// instance spec. Turn zero of the Tailor's living draft (phased), and the whole
// story for the non-phased verbatim view. Null only when the plan has no
// canonical url, an unrecognized shape, or (phased) no actions.
export function seedInstanceSpec(
  pd: PlanDefinition,
  ctx: DecodeContext | undefined,
  args: SeedInstanceArgs,
): PlanInstanceSpec | null {
  const templateCanonical = planCanonical(pd);
  if (!templateCanonical) {
    return null;
  }
  const shape = planShapeOf(pd);
  if (!shape) {
    return null;
  }

  // Both clinical goals and (continuous) control bands are FHIR goals on the
  // template, so this captures both with no special-casing.
  const goals = (pd.goal ?? [])
    .map(fromFhirGoal)
    .filter((g): g is PlanGoal => g !== undefined);

  // Non-phased plans aren't tailored: the instance IS the template bound to a
  // patient. Seed the minimal verbatim spec; the read-only view renders the
  // template directly (ENG-4773).
  if (shape !== 'phased') {
    const base = {
      title: pd.title ?? pd.name ?? 'Care plan',
      templateCanonical,
      startDate: args.startDate,
      goals: goals.length > 0 ? goals : undefined,
    };
    return shape === 'continuous'
      ? { shape: 'continuous', ...base }
      : { shape: 'episodic', ...base };
  }

  const actions = pd.action ?? [];
  if (actions.length === 0) {
    return null;
  }

  const phaseCodeSystem = actions
    .map((a) => phaseCoding(a)?.system)
    .find((s) => s !== undefined);

  // The CURRENT phase's schedule, not the whole plan's (ENG-5748).
  const { segments, reassessAfterVisits } = currentPhaseCadence(
    sessionFrequencyOf(pd),
    actions.map((action) => ({
      cadence: fromTiming(action.timingTiming).cadence,
      durationDays: actionDurationDays(action),
      visitsToComplete: actionVisitTarget(action),
    })),
  );

  return {
    shape: 'phased',
    title: pd.title ?? pd.name ?? 'Care plan',
    templateCanonical,
    phaseCodeSystem,
    startDate: args.startDate,
    goals: goals.length > 0 ? goals : undefined,
    sessionFrequency: segments,
    reassessment: { afterVisits: reassessAfterVisits, bookedManually: false },
    phases: actions.map((action) => {
      const coding = phaseCoding(action);
      return {
        title: action.title ?? coding?.display ?? 'Phase',
        code:
          coding?.code ?? (action.title ? slugify(action.title) : undefined),
        description: action.description,
        durationDays: actionDurationDays(action),
        visitsToComplete: actionVisitTarget(action),
        activities: seedActivities(action.action, ctx),
      };
    }),
  };
}

// --- Compile: spec → instance resources (the materializer) -------------------

// Lifecycle posture (ADR-0013, ENG-4699): the unattended end-of-encounter draft
// is born a `proposal` (CarePlan draft/proposal, Goals proposed, every SR
// draft) for the clinician to review; `accepted` is the in-place flip on accept
// (CarePlan active/plan, Goals active, the current phase's SRs active, future
// phases still draft — phase advance is status choreography, not minting).
export type CarePlanPosture = 'proposal' | 'accepted';

export type CompileInstanceArgs = {
  // FHIR reference like "Patient/<id>".
  patientRef: string;
  // FHIR reference like "Practitioner/<id>" — the tailoring/assigning clinician.
  authorRef?: string;
  authorDisplay?: string;
  posture: CarePlanPosture;
};

export type CompiledInstance = {
  // Created first; instanceSaveBundle wires carePlan.goal to them.
  goals: Goal[];
  carePlan: CarePlan;
  // Every phase's prescriptions, fully materialized: current phase active when
  // accepted, future phases draft — phase advance is status choreography.
  serviceRequests: ServiceRequest[];
  // One phase spine stick per phase (ADR-0016), index-aligned with
  // carePlan.activity[] through the cpl-3-safe stick extension. The searchable
  // phase status + the Encounter.basedOn anchor.
  phaseServiceRequests: ServiceRequest[];
};

/**
 * Mark a compile's children as machine-authored before they are written. Review
 * surfaces read MANUAL authorship as the ABSENCE of the agent tag, so an
 * untagged compiled row becomes a "manually added" one the moment a clinician
 * decides it. The CarePlan is left alone: it is not a reviewable row.
 */
export function agentAuthoredInstance(
  compiled: CompiledInstance,
  agentId: string,
): CompiledInstance {
  const stamp = <T extends Goal | ServiceRequest>(resource: T): T =>
    withAgentTag(resource, agentId);
  return {
    ...compiled,
    goals: compiled.goals.map(stamp),
    serviceRequests: compiled.serviceRequests.map(stamp),
    phaseServiceRequests: compiled.phaseServiceRequests.map(stamp),
  };
}

// Stick status choreography: a proposal keeps everything draft; an accepted
// plan has exactly one active stick (the current phase), completed before it,
// on-hold after — phase advance flips these, never mints.
function phaseStickStatus(
  index: number,
  firstLiveIndex: number,
  posture: CarePlanPosture,
): ServiceRequest['status'] {
  if (posture === 'proposal') return 'draft';
  if (index < firstLiveIndex) return 'completed';
  if (index === firstLiveIndex) return 'active';
  return 'on-hold';
}

// The per-phase spine stick (ADR-0016): ONE ServiceRequest{intent:'plan'} per
// phase — phase identity + status on standard indexed search params, the home
// for visits-to-complete, and the only legal Encounter.basedOn anchor (R4
// forbids pointing an Encounter at a CarePlan). `intent:'plan'` + no performer
// distinguishes it from prescribed leaf orders; phase association rides its
// `category` (not the leaves' careplan-activity-phase extension).
function phaseSpineStick(
  phase: InstancePhaseSpec,
  coding: Coding,
  status: ServiceRequest['status'],
  period: { start?: string; end?: string } | undefined,
  spec: PhasedInstanceSpec,
  args: CompileInstanceArgs,
): ServiceRequest {
  return {
    resourceType: 'ServiceRequest',
    status,
    intent: 'plan',
    subject: { reference: args.patientRef },
    ...(args.authorRef
      ? {
          requester: { reference: args.authorRef, display: args.authorDisplay },
        }
      : {}),
    authoredOn: spec.startDate,
    instantiatesCanonical: [spec.templateCanonical],
    category: [
      {
        coding: [
          {
            system: CAREPLAN_SEGMENT_SYSTEM,
            code: CAREPLAN_PHASE_SEGMENT_CODE,
          },
        ],
      },
      { coding: [coding] },
    ],
    code: { text: phase.title },
    ...(period ? { occurrencePeriod: period } : {}),
    ...(phase.visitsToComplete !== undefined
      ? { quantityQuantity: { value: phase.visitsToComplete, unit: 'visits' } }
      : {}),
  };
}

function activityToServiceRequest(
  activity: InstanceActivitySpec,
  phase: Coding,
  currentPhase: boolean,
  spec: PlanInstanceSpec,
  args: CompileInstanceArgs,
): ServiceRequest {
  const dosageExt = activity.dosage
    ? toDosageExtension(activity.dosage)
    : undefined;
  return {
    resourceType: 'ServiceRequest',
    // Future phases are materialized but not yet actionable; they activate at
    // phase advance. A proposal's current phase is draft too — nothing is live
    // until the clinician accepts.
    status: currentPhase && args.posture === 'accepted' ? 'active' : 'draft',
    intent: 'order',
    subject: { reference: args.patientRef },
    // Patient as performer is the idiomatic encoding of "home exercise".
    performer: [{ reference: args.patientRef }],
    ...(args.authorRef
      ? {
          requester: { reference: args.authorRef, display: args.authorDisplay },
        }
      : {}),
    authoredOn: spec.startDate,
    code: {
      text: activity.name,
      coding: activity.code
        ? [
            {
              system: activity.code.system,
              code: activity.code.code,
              display: activity.code.text,
            },
          ]
        : undefined,
    },
    ...(activity.template
      ? { instantiatesCanonical: [activity.template] }
      : {}),
    ...(activity.frequency
      ? { occurrenceTiming: toTiming({ cadence: activity.frequency }) }
      : {}),
    ...(activity.patientInstructions
      ? { patientInstruction: activity.patientInstructions }
      : {}),
    ...(activity.description ? { note: [{ text: activity.description }] } : {}),
    extension: [
      { url: ACTIVITY_PHASE_EXTENSION_URL, valueCoding: phase },
      ...(dosageExt ? [dosageExt] : []),
    ],
  };
}

// Goals materialize the same way for every shape: proposed on a draft, active
// once accepted. Shared by the phased and verbatim compile paths.
function compileInstanceGoals(
  goals: PlanGoal[] | undefined,
  args: CompileInstanceArgs,
): Goal[] {
  const accepted = args.posture === 'accepted';
  return (goals ?? []).map((goal) => ({
    resourceType: 'Goal',
    lifecycleStatus: accepted ? 'active' : 'proposed',
    description: { text: goal.description },
    subject: { reference: args.patientRef },
    ...(args.authorRef
      ? {
          expressedBy: {
            reference: args.authorRef,
            display: args.authorDisplay,
          },
        }
      : {}),
    ...(goal.targets && goal.targets.length > 0
      ? { target: goal.targets.map(toGoalTarget) }
      : {}),
  }));
}

/**
 * The authoring clinician's compartment stamp, as `meta.accounts`.
 *
 * `author` alone cannot scope a CarePlan: FHIR R4 gives CarePlan no `author`
 * search parameter (Medplum answers `CarePlan?author=…` with "Unknown search
 * parameter"), so the tenant-member AccessPolicy scopes it by
 * `_compartment=%profile`, which reads `meta.accounts`. Stamped here alongside
 * `author` so the two can never drift apart. Mirrors Goal (ENG-4997).
 *
 * Only the backend service client may set this — it writes with no AccessPolicy.
 * A clinician's own membership is bound to the compartment criteria, so it may
 * only write accounts that already include itself (which is why
 * {@link instanceReplaceBundle} carries the existing stamp forward rather than
 * re-deriving it).
 */
function authorCompartment(args: CompileInstanceArgs): Pick<CarePlan, 'meta'> {
  return args.authorRef
    ? { meta: { accounts: [{ reference: args.authorRef }] } }
    : {};
}

// Deterministic materialization: spec → the exact resources Save inserts.
// present_plan_instance runs Medplum $validate on every resource, so a clean
// draft is guaranteed to insert.
export function compileInstanceSpec(
  spec: PlanInstanceSpec,
  args: CompileInstanceArgs,
): CompiledInstance {
  // A non-phased instance is verbatim: just the CarePlan + its goals (control
  // bands included as ranged goals), no phase activities, no ServiceRequests.
  if (spec.shape !== 'phased') {
    const accepted = args.posture === 'accepted';
    const carePlan: CarePlan = {
      resourceType: 'CarePlan',
      status: accepted ? 'active' : 'draft',
      intent: accepted ? 'plan' : 'proposal',
      title: spec.title,
      instantiatesCanonical: [spec.templateCanonical],
      subject: { reference: args.patientRef },
      period: { start: spec.startDate },
      ...(args.authorRef
        ? { author: { reference: args.authorRef, display: args.authorDisplay } }
        : {}),
      ...authorCompartment(args),
    };
    return {
      goals: compileInstanceGoals(spec.goals, args),
      carePlan,
      serviceRequests: [],
      phaseServiceRequests: [],
    };
  }

  const accepted = args.posture === 'accepted';
  const system = spec.phaseCodeSystem ?? ELUVE_PHASE_SYSTEM;
  const activities: CarePlanActivity[] = [];
  const serviceRequests: ServiceRequest[] = [];
  const phaseServiceRequests: ServiceRequest[] = [];

  // The patient's current phase is the first one that isn't skipped
  // (durationDays 0 = "skip this phase outright") — its prescriptions are the
  // actionable ones once accepted; everything later stays draft until advance.
  // Stays -1 when every phase is skipped, so nothing is marked in-progress/active
  // (don't floor to 0, which would activate the first skipped phase).
  const firstLiveIndex = spec.phases.findIndex(
    (phase) => phase.durationDays !== 0,
  );

  let cursor: string | undefined = spec.startDate;
  spec.phases.forEach((phase, index) => {
    const coding: Coding = {
      system,
      code: phase.code ?? slugify(phase.title),
      display: phase.title,
    };
    const start = cursor;
    const end =
      start !== undefined && phase.durationDays !== undefined
        ? addDays(start, phase.durationDays)
        : undefined;
    cursor = end;
    activities.push({
      detail: {
        status: index === firstLiveIndex ? 'in-progress' : 'not-started',
        code: { coding: [coding], text: phase.title },
        ...(phase.description ? { description: phase.description } : {}),
        ...(start || end
          ? {
              scheduledPeriod: {
                ...(start ? { start } : {}),
                ...(end ? { end } : {}),
              },
            }
          : {}),
      },
    });
    phaseServiceRequests.push(
      phaseSpineStick(
        phase,
        coding,
        phaseStickStatus(index, firstLiveIndex, args.posture),
        start || end
          ? { ...(start ? { start } : {}), ...(end ? { end } : {}) }
          : undefined,
        spec,
        args,
      ),
    );
    for (const activity of phase.activities ?? []) {
      serviceRequests.push(
        activityToServiceRequest(
          activity,
          coding,
          index === firstLiveIndex,
          spec,
          args,
        ),
      );
    }
  });

  const goals = compileInstanceGoals(spec.goals, args);

  const planExtensions: Extension[] = [
    ...(spec.sessionFrequency && spec.sessionFrequency.length > 0
      ? [sessionFrequencyExtension(spec.sessionFrequency)]
      : []),
    ...(spec.reassessment
      ? [reassessmentMarkerExtension(spec.reassessment)]
      : []),
  ];

  const carePlan: CarePlan = {
    resourceType: 'CarePlan',
    status: accepted ? 'active' : 'draft',
    intent: accepted ? 'plan' : 'proposal',
    title: spec.title,
    instantiatesCanonical: [spec.templateCanonical],
    subject: { reference: args.patientRef },
    period: { start: spec.startDate },
    ...(args.authorRef
      ? { author: { reference: args.authorRef, display: args.authorDisplay } }
      : {}),
    ...authorCompartment(args),
    ...(activities.length > 0 ? { activity: activities } : {}),
    // The per-plan cadence snapshot (snapshot-not-reference: tailoring or the
    // schedule editor can change it without touching the template).
    ...(planExtensions.length > 0 ? { extension: planExtensions } : {}),
  };

  return { goals, carePlan, serviceRequests, phaseServiceRequests };
}

// --- Decode: instance resources → spec ----------------------------------------

function fromInstanceGoal(goal: Goal): PlanGoal | undefined {
  const description =
    goal.description?.text ?? goal.description?.coding?.[0]?.display;
  if (!description) {
    return undefined;
  }
  const targets = (goal.target ?? [])
    .map(fromGoalTarget)
    .filter((t): t is GoalTarget => t !== undefined);
  return { description, targets: targets.length > 0 ? targets : undefined };
}

export function decodeInstanceSpec(
  carePlan: CarePlan,
  serviceRequests: ServiceRequest[],
  goals: Goal[],
): PhasedInstanceSpec | null {
  const templateCanonical = carePlan.instantiatesCanonical?.[0];
  if (!templateCanonical) {
    return null;
  }

  // The same grouping the renderers use, minus the render-only SR status (the
  // spec derives status from phase position at compile).
  const byPhase = new Map<string, InstanceActivitySpec[]>(
    [...instanceActivitiesByPhase(serviceRequests)].map(
      ([code, activities]) => [
        code,
        // Drop the render-only fields (SR status + serviceRequestId); the spec
        // keeps only authored shape.
        activities.map(
          ({ status: _status, serviceRequestId: _srId, ...spec }) => spec,
        ),
      ],
    ),
  );

  // Per-phase visit targets recovered from the spine sticks riding in the same
  // based-on=CarePlan fetch as the leaf requests.
  const visitsByPhase = new Map<string, number>();
  for (const sr of serviceRequests) {
    if (!isSpineStick(sr)) continue;
    const code = serviceRequestSegmentPhaseCoding(sr)?.code;
    const visits = sr.quantityQuantity?.value;
    if (code && visits !== undefined) visitsByPhase.set(code, visits);
  }

  const phases: InstancePhaseSpec[] = [];
  let phaseCodeSystem: string | undefined;
  for (const activity of carePlan.activity ?? []) {
    const coding = activityPhaseCoding(activity);
    if (!coding?.code) {
      continue;
    }
    phaseCodeSystem ??= coding.system;
    const start = activity.detail?.scheduledPeriod?.start;
    const end = activity.detail?.scheduledPeriod?.end;
    const activities = byPhase.get(coding.code);
    phases.push({
      title: coding.display ?? activity.detail?.code?.text ?? 'Phase',
      code: coding.code,
      description: activity.detail?.description,
      durationDays: start && end ? diffDays(start, end) : undefined,
      visitsToComplete: visitsByPhase.get(coding.code),
      activities,
    });
  }
  if (phases.length === 0) {
    return null;
  }

  const decodedGoals = goals
    .map(fromInstanceGoal)
    .filter((g): g is PlanGoal => g !== undefined);

  return {
    shape: 'phased',
    title: carePlan.title ?? 'Care plan',
    templateCanonical,
    phaseCodeSystem,
    startDate: carePlan.period?.start ?? '',
    goals: decodedGoals.length > 0 ? decodedGoals : undefined,
    sessionFrequency: sessionFrequencyOf(carePlan),
    // Activation recompiles from this spec, so omitting it would silently reset
    // the clinician's reassessment point to the template default.
    reassessment: reassessmentMarkerOf(carePlan),
    phases,
  };
}

// --- Diff: seed vs draft (computed variance, ADR-0013) -------------------------

export type TailorOrigin = 'template' | 'tailored' | 'ad-hoc';

export type ActivityDiff = {
  name: string;
  origin: TailorOrigin;
  // Human-readable field labels; non-empty only when origin is "tailored".
  changedFields: string[];
};

export type PhaseDiff = {
  title: string;
  durationChanged: boolean;
  activities: ActivityDiff[];
  // Names of seed activities absent from the draft — deliberately not selected.
  skipped: string[];
};

export type GoalDiff = {
  description: string;
  origin: TailorOrigin;
  changedFields: string[];
};

export type InstanceDiff = {
  startDateChanged: boolean;
  titleChanged: boolean;
  phases: PhaseDiff[];
  goals: GoalDiff[];
  // Descriptions of seed goals absent from the draft.
  droppedGoals: string[];
  hasChanges: boolean;
};

function fieldEqual(a: unknown, b: unknown): boolean {
  return JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
}

function activityChanges(
  seed: InstanceActivitySpec,
  draft: InstanceActivitySpec,
): string[] {
  const changes: string[] = [];
  if (seed.name !== draft.name) {
    changes.push('name');
  }
  if (!fieldEqual(seed.dosage, draft.dosage)) {
    changes.push('dosage');
  }
  if (!fieldEqual(seed.frequency, draft.frequency)) {
    changes.push('frequency');
  }
  if (!fieldEqual(seed.patientInstructions, draft.patientInstructions)) {
    changes.push('instructions');
  }
  if (!fieldEqual(seed.description, draft.description)) {
    changes.push('description');
  }
  if (!fieldEqual(seed.code, draft.code)) {
    changes.push('code');
  }
  return changes;
}

// Render-time variance computation: the review surface's
// template-derived / tailored / ad-hoc / skipped marking, taken against the
// seed (the template version pinned at tailoring time). Never stored.
export function diffInstanceSpec(
  seed: PlanInstanceSpec,
  draft: PlanInstanceSpec,
): InstanceDiff {
  // The diff is phased-only — the verbatim (continuous/episodic) view never
  // tailors, so there's nothing to compute against. A non-phased spec has no
  // phase spine; return a no-change diff.
  if (seed.shape !== 'phased' || draft.shape !== 'phased') {
    return {
      startDateChanged: false,
      titleChanged: false,
      phases: [],
      goals: [],
      droppedGoals: [],
      hasChanges: false,
    };
  }

  // Matching is PHASE-SCOPED: the same exercise recurs across phases under one
  // template canonical, so the canonical alone is ambiguous — each occurrence
  // matches within its phase (the spine is fixed, so phase indexes align).
  // Activities the seed couldn't pin fall back to a per-phase name match.
  const seedByKey = new Map<string, InstanceActivitySpec>();
  const keyOf = (phaseIndex: number, activity: InstanceActivitySpec) =>
    `${phaseIndex}:${activity.template ?? `name:${activity.name}`}`;
  seed.phases.forEach((phase, phaseIndex) => {
    for (const activity of phase.activities ?? []) {
      seedByKey.set(keyOf(phaseIndex, activity), activity);
    }
  });

  const claimed = new Set<string>();
  const phases: PhaseDiff[] = draft.phases.map((phase, phaseIndex) => {
    const seedPhase =
      seed.phases.find((p) => p.code !== undefined && p.code === phase.code) ??
      seed.phases[phaseIndex];
    const activities: ActivityDiff[] = (phase.activities ?? []).map(
      (activity) => {
        const key = keyOf(phaseIndex, activity);
        const seedActivity = seedByKey.get(key);
        if (!seedActivity) {
          return { name: activity.name, origin: 'ad-hoc', changedFields: [] };
        }
        claimed.add(key);
        const changes = activityChanges(seedActivity, activity);
        return {
          name: activity.name,
          origin: changes.length > 0 ? 'tailored' : 'template',
          changedFields: changes,
        };
      },
    );
    return {
      title: phase.title,
      durationChanged: !fieldEqual(seedPhase?.durationDays, phase.durationDays),
      activities,
      skipped: [],
    };
  });

  // Unclaimed seed activities surface as skipped on their seed phase.
  seed.phases.forEach((phase, phaseIndex) => {
    for (const activity of phase.activities ?? []) {
      if (!claimed.has(keyOf(phaseIndex, activity))) {
        phases[phaseIndex]?.skipped.push(activity.name);
      }
    }
  });

  // Goals match on description, falling back to a shared target measure (a
  // personalized goal usually keeps the measure even when reworded). Two
  // passes: exact-description claims settle FIRST so a new goal sharing a
  // measure with an untouched template goal can't steal its match.
  const seedGoals = [...(seed.goals ?? [])];
  const claimedGoals = new Set<PlanGoal>();
  const matches = new Map<PlanGoal, PlanGoal>();
  for (const goal of draft.goals ?? []) {
    const byDescription = seedGoals.find(
      (g) => !claimedGoals.has(g) && g.description === goal.description,
    );
    if (byDescription) {
      claimedGoals.add(byDescription);
      matches.set(goal, byDescription);
    }
  }
  for (const goal of draft.goals ?? []) {
    if (matches.has(goal)) {
      continue;
    }
    const measures = new Set((goal.targets ?? []).map((t) => t.measure.text));
    const byMeasure = seedGoals.find(
      (g) =>
        !claimedGoals.has(g) &&
        (g.targets ?? []).some((t) => measures.has(t.measure.text)),
    );
    if (byMeasure) {
      claimedGoals.add(byMeasure);
      matches.set(goal, byMeasure);
    }
  }
  const goals: GoalDiff[] = (draft.goals ?? []).map((goal) => {
    const match = matches.get(goal);
    if (!match) {
      return {
        description: goal.description,
        origin: 'ad-hoc',
        changedFields: [],
      };
    }
    const changes: string[] = [];
    if (match.description !== goal.description) {
      changes.push('description');
    }
    if (!fieldEqual(match.targets, goal.targets)) {
      changes.push('targets');
    }
    return {
      description: goal.description,
      origin: changes.length > 0 ? 'tailored' : 'template',
      changedFields: changes,
    };
  });
  const droppedGoals = seedGoals
    .filter((g) => !claimedGoals.has(g))
    .map((g) => g.description);

  const startDateChanged = seed.startDate !== draft.startDate;
  const titleChanged = seed.title !== draft.title;
  const hasChanges =
    startDateChanged ||
    titleChanged ||
    droppedGoals.length > 0 ||
    goals.some((g) => g.origin !== 'template') ||
    phases.some(
      (p) =>
        p.durationChanged ||
        p.skipped.length > 0 ||
        p.activities.some((a) => a.origin !== 'template'),
    );

  return {
    startDateChanged,
    titleChanged,
    phases,
    goals,
    droppedGoals,
    hasChanges,
  };
}

// --- Save bundle: one transaction, browser- and server-executable --------------

// Stick references use an extension because cpl-3 forbids activity.reference
// beside detail; Medplum $validate enforces the invariant.
function zipActivitySticks(
  activities: CarePlanActivity[],
  stickEntries: BundleEntry[],
): CarePlanActivity[] {
  return activities.map((activity, i) => {
    const stick = stickEntries[i];
    if (!stick?.fullUrl) return activity;
    return {
      ...activity,
      extension: [
        ...(activity.extension ?? []),
        {
          url: ACTIVITY_STICK_EXTENSION_URL,
          valueReference: { reference: stick.fullUrl },
        },
      ],
    };
  });
}

// shared write path for the Tailor's browser-side save AND the unattended
// server-side draft (one materializer, one representation). Optional `tags`
// stamp meta.tag on every created resource (agent-run/thread ids).
export function instanceSaveBundle(
  compiled: CompiledInstance,
  tags?: Coding[],
): Bundle {
  const meta = tags && tags.length > 0 ? { tag: tags } : undefined;
  // Merge, don't replace: the compiled CarePlan already carries the author's
  // `meta.accounts` compartment stamp, and overwriting it here would leave the
  // plan outside the tenant-member `_compartment=%profile` criteria — invisible
  // to the clinician who authored it, and unwritable on activation (ENG-5699).
  const withMeta = <T extends Goal | CarePlan | ServiceRequest>(
    resource: T,
  ): T =>
    meta ? { ...resource, meta: { ...resource.meta, ...meta } } : resource;

  const goalEntries: BundleEntry[] = compiled.goals.map((goal) => ({
    fullUrl: `urn:uuid:${crypto.randomUUID()}`,
    resource: withMeta(goal),
    request: { method: 'POST', url: 'Goal' },
  }));

  const carePlanUrn = `urn:uuid:${crypto.randomUUID()}`;

  // Build sticks first so the CarePlan's activity[] can reference their urns,
  // index-aligned with compile's activity list (ADR-0016). Transaction entries
  // may appear later because FHIR resolves intra-bundle references by fullUrl.
  const stickEntries: BundleEntry[] = compiled.phaseServiceRequests.map(
    (sr) => ({
      fullUrl: `urn:uuid:${crypto.randomUUID()}`,
      resource: withMeta({ ...sr, basedOn: [{ reference: carePlanUrn }] }),
      request: { method: 'POST', url: 'ServiceRequest' },
    }),
  );

  const carePlanEntry: BundleEntry = {
    fullUrl: carePlanUrn,
    resource: withMeta({
      ...compiled.carePlan,
      ...(compiled.carePlan.activity
        ? {
            activity: zipActivitySticks(
              compiled.carePlan.activity,
              stickEntries,
            ),
          }
        : {}),
      ...(goalEntries.length > 0
        ? { goal: goalEntries.map((entry) => ({ reference: entry.fullUrl })) }
        : {}),
    }),
    request: { method: 'POST', url: 'CarePlan' },
  };

  const srEntries: BundleEntry[] = compiled.serviceRequests.map((sr) => ({
    fullUrl: `urn:uuid:${crypto.randomUUID()}`,
    resource: withMeta({ ...sr, basedOn: [{ reference: carePlanUrn }] }),
    request: { method: 'POST', url: 'ServiceRequest' },
  }));

  return {
    resourceType: 'Bundle',
    type: 'transaction',
    entry: [...goalEntries, carePlanEntry, ...srEntries, ...stickEntries],
  };
}

// Re-instantiate an existing draft in place from a freshly compiled instance
// (ENG-4764, the "switch to a different matched plan" path). The CarePlan keeps
// its id/identifier/encounter/extension so the tab, callout, and Tailor still
// resolve it; its body (title, instantiatesCanonical, phases) and children are
// swapped for the new plan's. One transaction: delete the old proposed children,
// mint the new Goals, PUT the reseeded CarePlan, POST the new ServiceRequests.
// Mirrors the browser-side `buildAcceptBundle`, but server-executable and
// posture-agnostic (the caller compiles a `proposal` to keep it a draft).
export function instanceReplaceBundle(params: {
  existing: CarePlan;
  existingGoals: Goal[];
  existingServiceRequests: ServiceRequest[];
  compiled: CompiledInstance;
  // The match extension to stamp (the rewritten winner + preserved candidate
  // pool). Replaces the CarePlan's extension array wholesale when provided.
  extension?: Extension[];
}): Bundle {
  const { existing, existingGoals, existingServiceRequests, compiled } = params;
  const carePlanId = existing.id;
  if (!carePlanId) {
    throw new Error('Cannot replace a CarePlan that has no id');
  }

  const entries: BundleEntry[] = [];

  // Drop the old proposed children first; the new set is minted fresh.
  for (const sr of existingServiceRequests) {
    if (sr.id) {
      entries.push({
        request: { method: 'DELETE', url: `ServiceRequest/${sr.id}` },
      });
    }
  }
  for (const goal of existingGoals) {
    if (goal.id) {
      entries.push({ request: { method: 'DELETE', url: `Goal/${goal.id}` } });
    }
  }

  const goalEntries: BundleEntry[] = compiled.goals.map((goal) => ({
    fullUrl: `urn:uuid:${crypto.randomUUID()}`,
    resource: goal,
    request: { method: 'POST', url: 'Goal' },
  }));
  entries.push(...goalEntries);

  const carePlanRef = `CarePlan/${carePlanId}`;

  // A caller-supplied extension array replaces the plan's extensions (the
  // match-stamp path); otherwise preserve them. Cadence always comes from the
  // compiled spec.
  const baseExtension =
    params.extension ?? existing.extension ?? compiled.carePlan.extension ?? [];
  const mergedExtension = [
    ...baseExtension.filter(
      (extension) => extension.url !== SESSION_FREQUENCY_EXTENSION_URL,
    ),
    ...(compiled.carePlan.extension ?? []).filter(
      (extension) => extension.url === SESSION_FREQUENCY_EXTENSION_URL,
    ),
  ];

  // New sticks first, so the reseeded CarePlan's activity[] can zip to them
  // (the old sticks were deleted with the rest of the SR children above).
  const stickEntries: BundleEntry[] = compiled.phaseServiceRequests.map(
    (sr) => ({
      fullUrl: `urn:uuid:${crypto.randomUUID()}`,
      resource: { ...sr, basedOn: [{ reference: carePlanRef }] },
      request: { method: 'POST', url: 'ServiceRequest' },
    }),
  );
  entries.push(...stickEntries);

  entries.push({
    fullUrl: carePlanRef,
    resource: {
      ...compiled.carePlan,
      id: carePlanId,
      // The clinician compartment stamp (`meta.accounts`) must survive the
      // replace. Activation runs as the clinician's own membership, which is
      // bound to `CarePlan?_compartment=%profile` — a PUT whose body drops
      // `meta.accounts` puts the resource outside that criteria and Medplum
      // rejects the whole transaction with 403 Forbidden (verified against
      // medplum-server 5.1.26). The existing stamp wins over the recompiled
      // one: it is what the policy already admits, and a backfilled plan may
      // carry a stamp the recompile can't re-derive.
      //
      // The audit tags (`meta.tag`, the agent-run/thread ids stamped at create)
      // carry forward too — a PUT is a whole-resource replace, so anything left
      // out of the body is deleted. Only these two are named: the rest of `meta`
      // is server-managed (versionId, lastUpdated, author, project, compartment)
      // and echoing it back is noise Medplum recomputes anyway.
      ...(existing.meta?.accounts || existing.meta?.tag
        ? {
            meta: {
              ...compiled.carePlan.meta,
              ...(existing.meta.accounts
                ? { accounts: existing.meta.accounts }
                : {}),
              ...(existing.meta.tag ? { tag: existing.meta.tag } : {}),
            },
          }
        : {}),
      ...(existing.identifier ? { identifier: existing.identifier } : {}),
      ...(existing.encounter ? { encounter: existing.encounter } : {}),
      // Conditions merged onto the plan (merge-on-approval) live only on the
      // CarePlan — the recompile knows nothing of them, so they carry over.
      ...(existing.addresses ? { addresses: existing.addresses } : {}),
      ...(mergedExtension.length > 0 ? { extension: mergedExtension } : {}),
      ...(compiled.carePlan.activity
        ? {
            activity: zipActivitySticks(
              compiled.carePlan.activity,
              stickEntries,
            ),
          }
        : {}),
      ...(goalEntries.length > 0
        ? { goal: goalEntries.map((e) => ({ reference: e.fullUrl })) }
        : {}),
    },
    request: { method: 'PUT', url: carePlanRef },
  });

  for (const sr of compiled.serviceRequests) {
    entries.push({
      fullUrl: `urn:uuid:${crypto.randomUUID()}`,
      resource: { ...sr, basedOn: [{ reference: carePlanRef }] },
      request: { method: 'POST', url: 'ServiceRequest' },
    });
  }

  return { resourceType: 'Bundle', type: 'transaction', entry: entries };
}

// The created CarePlan's id out of the save response. Checks EVERY entry first:
// Medplum only processes `transaction` bundles atomically when the project has
// the `transaction-bundles` feature; without it the bundle runs as a non-atomic
// batch where individual entries fail with a 200 overall — silently partial
// materialization unless someone looks.
export function createdCarePlanId(result: Bundle): string {
  const entries = result.entry ?? [];
  const failed = entries.filter((entry) => {
    const status = Number.parseInt(entry.response?.status ?? '0', 10);
    return status >= 400;
  });
  if (failed.length > 0) {
    const detail = failed
      .map((entry) => entry.response?.status)
      .filter(Boolean)
      .join(', ');
    throw new Error(
      `Care plan save failed: ${failed.length} of ${entries.length} resources rejected (${detail})`,
    );
  }
  const created = entries
    .map((entry) => entry.resource)
    .find(
      (resource): resource is CarePlan => resource?.resourceType === 'CarePlan',
    );
  const id =
    created?.id ??
    entries
      .map((entry) => entry.response?.location)
      .find((location) => location?.startsWith('CarePlan/'))
      ?.split('/')[1];
  if (!id) {
    throw new Error('Care plan save returned no CarePlan');
  }
  return id;
}
